// GreatLeaf Management — institutional real estate asset & property management platform.

export const COMPANY = {
  name: "GreatLeaf Management",
  short: "GreatLeaf",
  tagline: "We maximize property value through operational excellence.",
  phone: "317-617-0305",
  phoneHref: "tel:+13176170305",
};

export const IMAGES = {
  hero: "https://images.pexels.com/photos/11861957/pexels-photo-11861957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  skyline: "https://images.unsplash.com/photo-1483653085484-eb63c9f02547?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  investors: "https://images.pexels.com/photos/5668492/pexels-photo-5668492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  team: "https://images.pexels.com/photos/10375937/pexels-photo-10375937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  meeting: "https://images.pexels.com/photos/7433919/pexels-photo-7433919.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  esg: "https://images.pexels.com/photos/32649853/pexels-photo-32649853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  esg2: "https://images.unsplash.com/photo-1521708266372-b3547456cc2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  commercial: "https://images.unsplash.com/photo-1690944851207-3f288c8fcd0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  lobby: "https://images.unsplash.com/photo-1621293954908-907159247fc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

export const ASSET_CLASSES = [
  { slug: "multifamily", name: "Multifamily", desc: "Institutional-grade operations for garden, mid-rise and high-rise communities.", image: "https://images.unsplash.com/photo-1612637968894-660373e23b03?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { slug: "build-to-rent", name: "Build-to-Rent", desc: "Purpose-built rental communities managed for durable, scalable yield.", image: "https://images.unsplash.com/photo-1543071293-d91175a68672?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { slug: "single-family", name: "Single-Family Rental", desc: "Portfolio management for scattered-site and consolidated SFR holdings.", image: "https://images.pexels.com/photos/36676879/pexels-photo-36676879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "student-housing", name: "Student Housing", desc: "Lease-up, turns and by-the-bed operations engineered for peak season velocity.", image: "https://images.unsplash.com/photo-1551361415-69c87624334f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { slug: "hospitality", name: "Hospitality & Short-Term", desc: "Full-service hospitality and short-term rental operations with revenue optimization.", image: "https://images.unsplash.com/photo-1621293954908-907159247fc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { slug: "mixed-use", name: "Mixed-Use", desc: "Integrated residential, retail and amenity management under one operating standard.", image: "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { slug: "commercial", name: "Commercial", desc: "Office, retail and mixed commercial operations with tenant-first service delivery.", image: "https://images.unsplash.com/photo-1690944851207-3f288c8fcd0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { slug: "community", name: "HOA & Community", desc: "Governance, compliance and financial stewardship for associations and communities.", image: "https://images.unsplash.com/photo-1519452575417-564c1401ecc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
];

export const AUDIENCES = [
  "Family Offices",
  "Institutional Investors",
  "Private Equity Funds",
  "REITs",
  "Developers",
  "Real Estate Investment Groups",
  "High-Net-Worth Owners",
  "Commercial Property Owners",
];

export const SERVICE_PILLARS = [
  {
    slug: "property-management",
    title: "Property Management",
    lead: "Day-to-day operational excellence that protects income and elevates the resident experience.",
    items: ["Leasing & Marketing", "Revenue Management", "Maintenance Operations", "Resident Experience", "Vendor Management", "Occupancy Optimization"],
  },
  {
    slug: "asset-management",
    title: "Asset Management",
    lead: "Strategic oversight that aligns operations with each investor's return objectives.",
    items: ["Multifamily Asset Management", "Financial Reporting", "Budgeting & Forecasting", "Owner Reporting", "Capital Improvement Management", "Performance Optimization"],
  },
  {
    slug: "portfolio-transition",
    title: "Investment & Transaction Support",
    lead: "Underwriting-grade diligence and seamless transitions across the asset lifecycle.",
    items: ["Due Diligence", "Acquisition Transition", "Disposition Support", "Portfolio Onboarding", "Operational Integration", "Owner Representation"],
  },
  {
    slug: "hospitality-management",
    title: "Hospitality Management",
    lead: "Hospitality and short-term rental operations run to institutional standards.",
    items: ["Short-Term Rental Management", "Dynamic Revenue Management", "Guest Experience", "Housekeeping & Turns", "Channel & Distribution", "Compliance"],
  },
];

// All granular capabilities (for Services page / SEO)
export const ALL_SERVICES = [
  "Multifamily Asset Management", "Property Management", "Build-to-Rent Communities",
  "Single-Family Rental Portfolio Management", "Student Housing", "Hospitality & Short-Term Rental Management",
  "Mixed-Use Management", "Commercial Property Operations", "HOA & Community Management",
  "Leasing & Marketing", "Revenue Management", "Maintenance Operations", "Capital Improvement Management",
  "Vendor Management", "Financial Reporting", "Budgeting & Forecasting", "Owner Reporting",
  "Due Diligence", "Acquisition Transition", "Disposition Support", "Occupancy Optimization",
  "Resident Experience", "ESG & Sustainability",
];

export const WHY = [
  { title: "Vertically Integrated", desc: "Property management, asset management and investment support operate as one accountable platform — not a hand-off between vendors." },
  { title: "Owner-Aligned", desc: "Transparent reporting and mandate-specific KPIs mean our incentives are tied to your long-term asset performance." },
  { title: "Data-Driven Operations", desc: "Revenue management, expense discipline and analytics inform every operating decision across the portfolio." },
  { title: "Institutional Standards", desc: "Underwriting-grade diligence, controls and compliance built for family offices, funds and REITs." },
];

export const METRICS = [
  { value: "95.8%", label: "Average portfolio occupancy" },
  { value: "58%", label: "Resident retention rate" },
  { value: "12.4%", label: "Average NOI growth, first full year" },
  { value: "99.2%", label: "On-time owner reporting" },
];

export const LIFECYCLE = [
  { n: "01", title: "Acquisition Transition", desc: "Diligence, onboarding and a controlled operational takeover with zero income disruption." },
  { n: "02", title: "Stabilization", desc: "Lease-up, staffing, systems and vendor alignment to reach underwritten occupancy." },
  { n: "03", title: "Value Creation", desc: "Capital improvement management and repositioning that compound rent and asset value." },
  { n: "04", title: "Performance Optimization", desc: "Revenue management, expense discipline and analytics that maximize NOI." },
  { n: "05", title: "Disposition Support", desc: "Reporting, data rooms and operational readiness that support a premium exit." },
];

export const PLATFORM = [
  { title: "Revenue Management", desc: "Dynamic pricing and lease-term strategy across the portfolio." },
  { title: "Financial Reporting", desc: "Institutional-grade statements, audit-ready and mandate-specific." },
  { title: "Maintenance & Capex", desc: "Preventive programs and capital project oversight in one system." },
  { title: "Owner Portal & Analytics", desc: "Real-time performance visibility from asset to portfolio level." },
];

export const ESG = [
  { title: "Energy & Efficiency", desc: "Utility benchmarking and efficiency retrofits that reduce operating cost and carbon." },
  { title: "Sustainable Capital", desc: "Capital improvement planning that prioritizes durable, resource-efficient systems." },
  { title: "Resident Wellbeing", desc: "Healthier communities through air quality, water and amenity standards." },
  { title: "Governance", desc: "Transparent controls, compliance and reporting aligned to investor ESG mandates." },
];

export const LEADERSHIP = [
  { name: "Christian", role: "Founder & Chief Executive Officer", bio: "Leads GreatLeaf's vision and investment strategy, aligning operations with long-term asset performance for institutional owners." },
  { name: "Managing Director, Asset Management", role: "Asset Management", bio: "Oversees portfolio strategy, budgeting and performance across the firm's managed assets." },
  { name: "Head of Operations", role: "Property Operations", bio: "Directs day-to-day operating standards, leasing and resident experience across all asset classes." },
  { name: "Director of Capital Projects", role: "Capital Improvements", bio: "Manages capital planning and value-creation programs from underwriting through delivery." },
];

export const AUDIENCE_TYPES = [
  "Family Office", "Institutional Investor", "Private Equity Fund", "REIT",
  "Developer", "Investment Group", "Private Owner", "Commercial Owner",
];
